import React, { createContext, useContext, useMemo } from "react"

import { useUser } from "@clerk/nextjs"

import type { Annotation } from "@/types/api/Annotation"

interface ReviewAnnotationMode {
  isReadOnly: boolean
  subjectEmail: string | null
}

const ReviewAnnotationModeContext = createContext<ReviewAnnotationMode | null>(
  null,
)

export const reviewAssigneeMatches = (
  left: string | null | undefined,
  right: string | null | undefined,
) => left?.trim().toLowerCase() === right?.trim().toLowerCase()

export const ReviewAnnotationModeProvider: React.FC<{
  children: React.ReactNode
  isReadOnly: boolean
  subjectEmail?: string
}> = ({ children, isReadOnly, subjectEmail }) => {
  const value = useMemo(
    () => ({ isReadOnly, subjectEmail: subjectEmail ?? null }),
    [isReadOnly, subjectEmail],
  )
  return (
    <ReviewAnnotationModeContext.Provider value={value}>
      {children}
    </ReviewAnnotationModeContext.Provider>
  )
}

export function useReviewAnnotationMode(): ReviewAnnotationMode {
  const mode = useContext(ReviewAnnotationModeContext)
  const { user } = useUser()
  return {
    isReadOnly: mode?.isReadOnly ?? false,
    subjectEmail:
      mode?.subjectEmail ?? user?.emailAddresses?.[0]?.emailAddress ?? null,
  }
}

export function useReviewSubjectAnnotation(
  annotations: Annotation[],
): Annotation | null {
  const { subjectEmail } = useReviewAnnotationMode()
  return useMemo(
    () =>
      annotations.find((annotation) =>
        reviewAssigneeMatches(annotation.assignee, subjectEmail),
      ) ?? null,
    [annotations, subjectEmail],
  )
}
